import { supabaseClient } from "../auth/supabase.service";
import { renderTextArrayEditor } from "./textArrayEditor.js";
import { resolveImageUrl } from "./utils.pages.js";
import * as icons from "./icons.js";

const { autorun, observable } = mobx;

/**
 * Base class for JSONB editors with shared functionality
 */
export class JsonbEditorBase {
  constructor(config) {
    this.dom = config.dom;
    this.message = config.message;
    this.heading = config.heading;
    this.comment = config.comment;
    this.table = config.table;
    this.recordId = config.recordId;
    this.fieldName = config.fieldName;
    this.base_url = config.base_url;
    this.onBeforeSave = config.onBeforeSave;
    this.onAfterLoad = config.onAfterLoad;
    this.validate = config.validate;
    this.filePicker = config.filePicker;
    this.fileNameByUrl = new Map();

    this.state = observable({
      data: null,
      originalData: null,
      isLoading: false,
      error: null
    });
  }

  // Data Model Layer
  async fetchData() {
    const { data, error } = await supabaseClient
      .from(this.table)
      .select(this.fieldName)
      .eq('id', this.recordId)
      .single();
    
    if (error) return { data: null, error };
    
    let result = data?.[this.fieldName];
    if (this.onAfterLoad) result = this.onAfterLoad(result);
    return { data: result, error: null };
  }

  async saveData(data) {
    let dataToSave = data;
    if (this.onBeforeSave) dataToSave = this.onBeforeSave(dataToSave);
    
    const { error } = await supabaseClient
      .from(this.table)
      .update({ [this.fieldName]: dataToSave })
      .eq('id', this.recordId);
    
    return { error };
  }

  async loadFileNameLookup() {
    if (!this.filePicker) {
      this.fileNameByUrl = new Map();
      return;
    }
    try {
      const { data, error } = await supabaseClient
        .from('files')
        .select('name, url');
      if (error) {
        this.fileNameByUrl = new Map();
        return;
      }
      this.fileNameByUrl = new Map((data || [])
        .filter(f => f?.url)
        .map(f => [f.url, f.name || f.url]));
    } catch (_) {
      this.fileNameByUrl = new Map();
    }
  }

  getFriendlyFileName(url = '') {
    return this.fileNameByUrl.get(url) || '';
  }

  // Shared utility functions
  renderFieldValue(container, fieldDef, value, showLabel = false) {
    if (!value && value !== 0) return;

    const fieldDiv = container.append('div')
      .attr('class', `field-${fieldDef.field || 'value'}`);
    
    // Add label if requested
    if (showLabel && fieldDef.label) {
      fieldDiv.append('span')
        .attr('class', 'field-label')
        .text(fieldDef.label + ': ');
    }
    
    if (fieldDef.type === 'url') {
      let displayText = value;
      if (fieldDef.displayshort !== null && fieldDef.displayshort !== undefined) {
        displayText = value.substring(0, fieldDef.displayshort);
      }
      fieldDiv.append('a')
        .attr('href', value)
        .attr('target', '_blank')
        .style('font-size', '0.9em')
        .style('word-break', 'break-all')
        .text(displayText);
    } else if (fieldDef.type === 'image') {
      const imageHtml = icons[value] || `<img src="${resolveImageUrl(value, this.base_url)}" alt="${fieldDef.label || fieldDef.field || 'Image'}" />`;
      fieldDiv.html(imageHtml);
    } else if (fieldDef.type === 'color') {
      const colorDiv = fieldDiv.append('div')
        .style('display', 'inline-flex')
        .style('align-items', 'center')
        .style('gap', '8px');
      
      colorDiv.append('div')
        .style('width', '40px')
        .style('height', '20px')
        .style('background-color', value)
        .style('border', '1px solid #ccc')
        .style('border-radius', '3px');
      
      colorDiv.append('span')
        .attr('class', 'field-value')
        .text(value);
    } else if (fieldDef.type === 'kvp' && typeof value === 'object' && value !== null) {
      const kvpText = Object.entries(value)
        .map(([k, v]) => `${k} ${v}`)
        .join(', ');
      fieldDiv.append('span')
        .attr('class', 'field-value kvp-value')
        .text(kvpText);
    } else if (fieldDef.type === 'textarray') {
      // Comma-separated text array: display with line breaks
      if (value) {
        value.forEach((v, i) => {
          fieldDiv.append('span')
            .attr('class', 'field-value')
            .text(v);
          if (i < value.length - 1) {
            fieldDiv.append('br');
          }
        });
      }
    } else {
      // text or number
      fieldDiv.append('span')
        .attr('class', 'field-value')
        .text(value);
    }
  }

  renderFormInput(container, fieldDef, idPrefix, currentValue) {
    const fieldId = `${idPrefix}-${fieldDef.field || fieldDef.key}`;

    if (fieldDef.type === 'image') {
      const actualUrl = currentValue || '';
      const friendlyName = this.getFriendlyFileName(actualUrl);
      const displayValue = friendlyName || actualUrl;

      const inputRow = container.append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('gap', '6px');

      const input = inputRow.append('input')
        .attr('type', 'text')
        .attr('placeholder', fieldDef.label)
        .attr('id', fieldId)
        .attr('class', 'input')
        .attr('data-url-value', actualUrl)
        .style('flex', '1')
        .property('value', displayValue)
        .on('input', function() {
          const previewWrapper = container.select('.preview-wrapper');
          const value = (this.value || '').trim();
          d3.select(this).attr('data-url-value', value);
          const imageHtml = icons[value] || `<img src="${resolveImageUrl(value, this.base_url)}" alt="Preview" />`;
          previewWrapper.html(imageHtml);
        }.bind(this))
        .on('blur', function() {
          if (!this.filePicker) return;
          const stored = d3.select(this).attr('data-url-value') || '';
          const friendly = this.getFriendlyFileName(stored);
          if (friendly) this.value = friendly;
        }.bind(this));
      
      // Add comment if provided
      if (fieldDef.comment) {
        container.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(fieldDef.comment);
      }
      
      const imageHtml = icons[currentValue] || `<img src="${resolveImageUrl(currentValue, this.base_url) || ''}" alt="Preview" />`;

      if (this.filePicker) {
        inputRow.append('div')
          .attr('class', 'button button-option bigger')
          .text('⋮')
          .on('click', () => {
            this.filePicker.open(file => {
              const url = file?.url || file;
              const name = file?.name;
              if (!url) return;
              const friendly = name || this.getFriendlyFileName(url) || url;
              input
                .attr('data-url-value', url)
                .property('value', friendly);
              const previewHtml = icons[url] || `<img src="${resolveImageUrl(url, this.base_url) || ''}" alt="Preview" />`;
              container.select('.preview-wrapper').html(previewHtml);
            });
          });
      }

      container.append('div')
        .attr('class', 'preview-wrapper')
        .style('margin-top', '6px')
        .html(imageHtml);
    } else if (fieldDef.type === 'color') {
      const colorInputGroup = container.append('div')
        .style('display', 'flex')
        .style('gap', '8px')
        .style('align-items', 'center');
      
      // Color picker
      colorInputGroup.append('input')
        .attr('type', 'color')
        .attr('id', `${fieldId}-picker`)
        .attr('class', 'color-picker')
        .attr('value', currentValue || '#000000')
        .on('input', function() {
          container.select(`#${fieldId}`).property('value', this.value);
        });
      
      // Text input for hex value
      colorInputGroup.append('input')
        .attr('type', 'text')
        .attr('id', fieldId)
        .attr('class', 'input')
        .attr('placeholder', '#000000')
        .style('flex', '1')
        .attr('value', currentValue || '')
        .on('input', function() {
          const value = this.value;
          if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
            container.select(`#${fieldId}-picker`).property('value', value);
          }
        });
      
      // Add comment if provided
      if (fieldDef.comment) {
        container.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(fieldDef.comment);
      }
    } else if (fieldDef.type === 'kvp' && fieldDef.keys) {
      const kvpContainer = container.append('div').attr('class', 'kvp-editor');
      const existingValue = currentValue || {};
      
      fieldDef.keys.forEach((key, index) => {
        const keyRow = kvpContainer.append('div')
          .attr('class', 'kvp-row')
          .style('margin-bottom', '6px')
          .style('display', 'flex')
          .style('align-items', 'center')
          .style('gap', '8px');
        
        const checkbox = keyRow.append('input')
          .attr('type', 'checkbox')
          .attr('id', `${fieldId}-${key}-enabled`)
          .attr('class', 'kvp-checkbox')
          .property('checked', existingValue.hasOwnProperty(key));
        
        keyRow.append('label')
          .attr('for', `${fieldId}-${key}-enabled`)
          .style('min-width', '120px')
          .text(key);
        
        const inputWrapper = keyRow.append('div')
          .style('flex', '1')
          .style('display', 'flex')
          .style('flex-direction', 'column');
        
        const input = inputWrapper.append('input')
          .attr('type', 'text')
          .attr('id', `${fieldId}-${key}-value`)
          .attr('class', 'input kvp-input')
          .property('disabled', !existingValue.hasOwnProperty(key))
          .attr('value', existingValue[key] || '');
        
        // Add comment if provided
        if (fieldDef.comment && fieldDef.comment[index]) {
          inputWrapper.append('small')
            .style('color', '#999')
            .style('font-size', '0.85em')
            .style('margin-top', '2px')
            .html(fieldDef.comment[index]);
        }
        
        checkbox.on('change', function() {
          input.property('disabled', !this.checked);
          if (!this.checked) input.property('value', '');
        });
      });
      
      // Hidden input to mark this as kvp field
      container.append('input')
        .attr('type', 'hidden')
        .attr('id', fieldId)
        .attr('data-kvp', 'true');
    } else if (fieldDef.type === 'boolean') {
      container.append('input')
        .attr('type', 'checkbox')
        .attr('id', fieldId)
        .attr('class', 'checkbox')
        .property('checked', currentValue === true);
      
      // Add comment if provided
      if (fieldDef.comment) {
        container.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(fieldDef.comment);
      }
    } else if (fieldDef.type === 'options' && fieldDef.values) {
      const select = container.append('select')
        .attr('id', fieldId)
        .attr('class', 'input');
      
      // Add empty option
      select.append('option')
        .attr('value', '')
        .text('-- Select --');
      
      // Add options from values
      fieldDef.values.forEach(value => {
        select.append('option')
          .attr('value', value)
          .property('selected', currentValue === value)
          .text(value);
      });
      
      // Add comment if provided
      if (fieldDef.comment) {
        container.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(fieldDef.comment);
      }
    } else if (fieldDef.type === 'textarray' && fieldDef.values) {
      this.renderTextArrayEditor(container, fieldDef, fieldId, currentValue);
      
      // Add comment if provided
      if (fieldDef.comment) {
        container.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(fieldDef.comment);
      }
    } else {
      const inputType = fieldDef.type === 'number' ? 'number' : 'text';
      container.append('input')
        .attr('type', inputType)
        .attr('placeholder', fieldDef.label)
        .attr('id', fieldId)
        .attr('class', 'input')
        .attr('value', currentValue || '');
      
      // Add comment if provided
      if (fieldDef.comment) {
        container.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(fieldDef.comment);
      }
    }
  }

  collectFormValue(form, fieldDef, idPrefix) {
    const fieldId = `${idPrefix}-${fieldDef.field || fieldDef.key}`;
    
    if (fieldDef.type === 'kvp' && fieldDef.keys) {
      const kvpObj = {};
      fieldDef.keys.forEach(key => {
        const checkbox = form.select(`#${fieldId}-${key}-enabled`).node();
        const input = form.select(`#${fieldId}-${key}-value`).node();
        if (checkbox?.checked && input?.value) {
          kvpObj[key] = input.value;
        }
      });
      return Object.keys(kvpObj).length > 0 ? kvpObj : null;
    } else if (fieldDef.type === 'boolean') {
      const checkbox = form.select(`#${fieldId}`).node();
      return checkbox?.checked || false;
    } else if (fieldDef.type === 'textarray' && fieldDef.values) {
      // Get the selection order array from the hidden input
      const hiddenInput = form.select(`#${fieldId}`).node();
      const selectionOrder = hiddenInput?._selectionOrder || [];
      return selectionOrder.length > 0 ? selectionOrder : null;
    } else {
      const input = form.select(`#${fieldId}`).node();
      if (fieldDef.type === 'image') {
        const storedUrl = input?.getAttribute('data-url-value');
        return storedUrl || input?.value || null;
      }
      return input?.value || null;
    }
  }

  /**
   * Render a textarray editor with checkboxes
   * @param {Object} container - D3 selection for container
   * @param {Object} fieldDef - Field definition with values array
   * @param {string} fieldId - ID for the field
   * @param {Array} currentValue - Current array value
   * @returns {Array} Selection order array reference
   */
  renderTextArrayEditor(container, fieldDef, fieldId, currentValue) {
    return renderTextArrayEditor(container, fieldDef, fieldId, currentValue);
  }

  async load() {
    this.state.isLoading = true;
    this.state.error = null;
    
    const { data, error } = await this.fetchData();
    
    if (error) {
      this.state.error = error;
      console.error('Error loading data', error);
      this.dom.html('');
      this.dom.append('p').text('Error loading data.');
      return;
    }
    
    this.state.data = data;
    this.state.originalData = JSON.parse(JSON.stringify(data));
    await this.loadFileNameLookup();
    this.state.isLoading = false;
    
    this.render();
  }

  // Abstract method to be implemented by subclasses
  render() {
    throw new Error('render() must be implemented by subclass');
  }
}
